import { useCallback, useState } from 'react';

import type {
  EditableGridCell,
  GridCell,
  Highlight,
  Item,
} from '@glideapps/glide-data-grid';

import { getTickerCellTemplate, isTickerCell } from '../../CustomCell/Ticker';
import { findMatchingTickerCellRows, getHighlightedRegions } from '../utils';
import { COLUMN_IDS } from '../constants';

import type { useTableState } from './useTableState';

const tickerColIndex = COLUMN_IDS.indexOf('ticker');

interface TickerRematchState {
  cell: Item;
  oldValue: ReturnType<typeof getTickerCellTemplate>;
  newValue: ReturnType<typeof getTickerCellTemplate>;
  matchingRows: number[];
  highlightRegions: Highlight[];
}

type Args = {
  rematchCell: ReturnType<typeof useTableState>['setCellValue'];
  onRematchCell: (rowIndex: number) => void;
  getTickerColumn: () => GridCell[] | undefined;
};

export const useTickerRematch = ({
  rematchCell,
  onRematchCell,
  getTickerColumn,
}: Args) => {
  const [tickerRematchState, setTickerRematchState] =
    useState<TickerRematchState | null>(null);

  const resetTickerRematchState = useCallback(() => {
    setTickerRematchState(null);
  }, []);

  const rematchTickerCells = useCallback(() => {
    tickerRematchState?.matchingRows.forEach((rowIndex) => {
      rematchCell([tickerColIndex, rowIndex], tickerRematchState.newValue, () =>
        onRematchCell(rowIndex)
      );
    });
    resetTickerRematchState();
  }, [onRematchCell, rematchCell, tickerRematchState, resetTickerRematchState]);

  const validateTickerCellRematch = useCallback(
    ([col, row]: Item, oldCell: GridCell, newCell: EditableGridCell) => {
      if (!isTickerCell(oldCell)) return;
      if (!isTickerCell(newCell)) return;

      const { uniqueSymbol: oldSymbol, status: oldStatus } = oldCell.data;
      const { uniqueSymbol: newSymbol, status: newStatus } = newCell.data;

      // Rematch only if:
      // 1. Old cell has a symbol or was in an error state
      // 2. New cell is in the success state
      // 3. Symbols are different
      if (
        (oldSymbol || oldStatus === 'error') &&
        newStatus === 'success' &&
        oldSymbol !== newSymbol
      ) {
        const matchingRows = findMatchingTickerCellRows(
          oldCell,
          getTickerColumn()
        );
        if (matchingRows.length === 0) return;

        setTickerRematchState({
          cell: [col, row],
          oldValue: oldCell,
          newValue: newCell,
          matchingRows,
          highlightRegions: getHighlightedRegions(
            matchingRows.map((rowIndex) => [tickerColIndex, rowIndex])
          ),
        });
      }
    },
    [getTickerColumn]
  );

  return {
    tickerRematchState,
    validateTickerCellRematch,
    rematchTickerCells,
    resetTickerRematchState,
  };
};
